import { FC, lazy, memo, Suspense, useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppBar, Button, IconButton, Stack, Typography } from '@mui/material';
import { Brightness4 as BrightnessIcon, Menu as MenuIcon, Timeline as TimelineIcon } from '@mui/icons-material';

import type { App } from '@playq/octopus2-apps';

import type { IThemeModeContext, SyntheticEventWithMetaKey } from '/common/models';
import { ThemeModeContext } from '/common/theme';
import { appToolkit, authToolkit, kbarToolkit, uiToolkit, fetchUserSettings } from '/store/toolkits';
import { useAppDispatch } from '/store';
import { AppIcon } from '/component/Apps/AppIcon';
import { ProgressIndicator } from '/common/ProgressIndicator';
import { OnlinePageUsers } from '/shared/OnlinePageUsers';
import { OnlineDevices } from '/shared/OnlineDevices';
import { InvokeAllScriptsButton } from '/component/Services/Scripts/InvokeAllScriptsButton';
import { navigateToURL } from '/helpers/navigation';

import { Notification } from './Notification';
import { SpacePicker } from './SpacePicker';
import { Corporate } from './Corporate';
import { Profile } from './Profile';
import {
  AppIconWrapper,
  AppInfo,
  AppName,
  headerHeight,
  LinkToHome,
  StyledDivider,
  StyledNav,
  StyledToolbar,
} from './styles';

export const AppPicker = lazy(() =>
  import(/* webpackChunkName: "AppPicker" */ '/component/Apps/AppsPicker').then((module) => ({
    default: module.AppPicker,
  }))
);

export const Header: FC = memo(() => {
  const { currentThemeMode, updateThemeMode }: IThemeModeContext = useContext(ThemeModeContext);

  const [appSelectOpen, setAppSelectOpen] = useState(false);

  const isAuthorizationSet: boolean = useSelector(authToolkit.selectors.isServicesAuthorizationSet);
  const isLoggedIn: boolean = useSelector(authToolkit.selectors.loggedIn);
  const app: App | undefined = useSelector(appToolkit.selectors.app);
  const openSelectAppModal = useSelector(kbarToolkit.selectors.openSelectAppModal);

  const appDispatch = useAppDispatch();

  useEffect(() => {
    if (isLoggedIn) {
      appDispatch(fetchUserSettings());
    }
  }, [appDispatch, isLoggedIn]);

  const handleAppSelectOpen = useCallback(
    (val: boolean) => () => {
      setAppSelectOpen(val);
      if (!val) {
        appDispatch(kbarToolkit.actions.setOpenSelectAppModal(false));
      }
    },
    [appDispatch]
  );

  useEffect(
    function handleSelectAppModalByKbar() {
      if (openSelectAppModal) {
        setAppSelectOpen(true);
      }
    },
    [appDispatch, handleAppSelectOpen, openSelectAppModal]
  );

  const handleToggleNavSidebar = () => appDispatch(uiToolkit.actions.toggleNavSidebar());

  const handleToggleTimeline = () => appDispatch(uiToolkit.actions.toggleTimeline());

  const updateThemeModeHandler = () => {
    if (currentThemeMode === 'dark') {
      updateThemeMode('light');
    } else {
      updateThemeMode('dark');
    }
  };

  const homeURL = '/';

  const handleGoHome = (event: SyntheticEventWithMetaKey) => navigateToURL({ url: homeURL, event });

  return (
    <>
      <AppBar
        position='fixed'
        sx={[
          (theme) => ({
            height: headerHeight,
            zIndex: theme.zIndex.drawer + 1,
          }),
        ]}
        enableColorOnDark={true}
      >
        <StyledToolbar variant='dense' disableGutters>
          <AppInfo>
            <ProgressIndicator />
            <IconButton onClick={handleToggleNavSidebar} color='inherit' aria-label='Toggle nav sidebar' size='large'>
              <MenuIcon />
            </IconButton>
            <Stack spacing={1} direction='row' alignItems='center'>
              <Typography noWrap={true} className='title' variant='h6'>
                <LinkToHome onClick={handleGoHome} underline='none' href={homeURL}>
                  PlayQ Octopus
                </LinkToHome>
              </Typography>
              <OnlinePageUsers />
              <OnlineDevices />
            </Stack>
          </AppInfo>
          <StyledNav>
            <Button
              onClick={handleAppSelectOpen(true)}
              startIcon={
                app ? (
                  <AppIconWrapper>
                    <AppIcon app={app} />
                  </AppIconWrapper>
                ) : null
              }
              color='inherit'
              aria-label='Select App'
              data-testid='select-app'
            >
              <AppName noWrap={true} color='inherit'>
                {app ? app.name : 'Select App'}
              </AppName>
            </Button>
            <SpacePicker />
            <StyledDivider orientation='vertical' />
            {app && (
              <>
                {isAuthorizationSet && (
                  <Suspense>
                    <InvokeAllScriptsButton />
                  </Suspense>
                )}

                <IconButton onClick={handleToggleTimeline} color='inherit' aria-label='Toggle timeline' size='large'>
                  <TimelineIcon />
                </IconButton>
              </>
            )}
            <Notification />
            <IconButton onClick={updateThemeModeHandler} color='inherit' aria-label='Select Theme' size='large'>
              <BrightnessIcon />
            </IconButton>
            <Corporate />
            <Profile />
          </StyledNav>
        </StyledToolbar>
      </AppBar>
      {appSelectOpen && (
        <Suspense>
          <AppPicker onClose={handleAppSelectOpen(false)} />
        </Suspense>
      )}
    </>
  );
});
